import { useEffect } from 'react';

import { CircleQuestionMark, Trash2 } from 'lucide-react';
import { useForm, type SubmitHandler, useFieldArray } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import CrudLayout from '../../../layout/CRUDLayout';
import { ApiResponse } from '../../../model';
import TextEditor from '../../../ui-components/TextEditor';

import {
  useAddEmailMutation,
  useLazyGetSingleEmailQuery,
  useUpdateEmailMutation,
} from './systemEmailApi';

import type { Inputs } from './model';

interface SystemEmailFormProps {
  embedded?: boolean;
  emailTemplateId?: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

const SystemEmailForm = ({
  embedded = false,
  emailTemplateId,
  onClose,
  onSuccess,
}: SystemEmailFormProps) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const resolvedId = emailTemplateId ?? id;
  const isEditMode = Boolean(resolvedId);

  const [addEmail, { isLoading }] = useAddEmailMutation();
  const [updateEmail, { isLoading: isUpdating }] = useUpdateEmailMutation();
  const [getEmail, { isLoading: isFetching }] = useLazyGetSingleEmailQuery();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<Inputs>({
    defaultValues: {
      emailTitle: '',
      emailCode: '',
      emailSubject: '',
      emailMessage: '',
      variables: [{ varName: '', varDesc: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variables',
  });

  useEffect(() => {
    if (isEditMode && resolvedId) {
      getEmail(resolvedId).then((resp) => {
        const data = resp?.data?.data;
        if (data) {
          setValue('emailTitle', data.emailTitle);
          setValue('emailCode', data.emailCode);
          setValue('emailSubject', data.emailSubject);
          setValue('emailMessage', data.emailMessage);
          if (data.variables) {
            setValue('variables', data.variables);
          }
        }
      });
    }
  }, [getEmail, isEditMode, resolvedId, setValue]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      const payload = { ...data };
      const response =
        isEditMode && resolvedId
          ? await updateEmail({ id: resolvedId, obj: payload }).unwrap()
          : await addEmail(payload).unwrap();
      toast.success(response?.settings?.message);
      if (embedded) {
        onSuccess?.();
        onClose?.();
      } else {
        navigate('/email');
      }
    } catch (err) {
      const e = err as ApiResponse<unknown>;
      toast.error(e?.settings?.message);
    }
  };

  if (isFetching) {
    return <div>Loading...</div>;
  }

  const subHeaderKey = isEditMode
    ? 'GENERIC_UPDATE_SUBHEADER'
    : 'GENERIC_ADD_SUBHEADER';

  const formFields = (
    <>
      <div className="mb-4">
        <label className="label">
          Title <span className="required">*</span>
        </label>
        <input
          {...register('emailTitle', {
            required: 'Title is required.',
          })}
          className="input"
          placeholder="Title"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        {errors.emailTitle && (
          <p className="error-msg">{errors.emailTitle.message}</p>
        )}
      </div>

      <div className="mb-4">
        <label className="label">
          Code <span className="required">*</span>
        </label>
        <input
          {...register('emailCode', {
            required: 'Code is required.',
            onChange: (e) => {
              const uppercaseValue = e.target.value.toUpperCase();
              setValue('emailCode', uppercaseValue);
            },
          })}
          readOnly={isEditMode}
          className={`input ${
            isEditMode
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : ''
          }`}
          placeholder="Code"
        />
        {errors.emailCode && (
          <p className="error-msg">{errors.emailCode.message}</p>
        )}
      </div>

      <div className="mb-4">
        <label className="label">
          Subject <span className="required">*</span>
        </label>
        <input
          {...register('emailSubject', {
            required: 'Subject is required.',
          })}
          className="input"
          placeholder="Subject"
        />
        {errors.emailSubject && (
          <p className="error-msg">{errors.emailSubject.message}</p>
        )}
      </div>

      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="top-title font-medium">Variables</h2>

          <div className="relative group">
            <button
              type="button"
              className="btn-primary"
              onClick={() => append({ varName: '', varDesc: '' })}
            >
              {t('SYSTEMEMAIL_MANAGE_ADD_VARIABLES')}
            </button>
            <div
              className="
    absolute top-[-25px] left-1/2 -translate-x-1/2 mt-[-2] px-2 py-1
    bg-black text-white text-xs rounded shadow-md
    opacity-0 group-hover:opacity-100 transition-opacity
    z-10 whitespace-nowrap
  "
            >
              {t('SYSTEMEMAIL_MANAGE_ADD_VARIABLES')}
            </div>
          </div>
        </div>

        <div className="border dark:border-gray-700 rounded overflow-hidden text-sm">
          <div className="grid grid-cols-3 bg-gray-100 dark:bg-gray-800 font-medium text-gray-700 dark:text-gray-200 border-b dark:border-gray-700">
            <div className="p-3 border-r dark:border-gray-700 flex items-center">
              Variable Name
              <div className="ml-1 group relative cursor-pointer">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  <CircleQuestionMark size={16} />
                </span>
                <div
                  className="
    absolute ml-28 top-[-5px] left-1 -translate-x-1/2 mt-[-2] px-2 py-1
    bg-black text-white text-xs rounded shadow-md
    opacity-0 group-hover:opacity-100 transition-opacity
    z-10 whitespace-nowrap
  "
                >
                  Example: #COMPANY_NAME#
                </div>
              </div>
              <span className="required ml-1">*</span>
            </div>
            <div className="p-3 border-r dark:border-gray-700">
              Variable Description <span className="required">*</span>
            </div>
            <div className="p-3">Action</div>
          </div>

          {fields.map((field, index) => (
            <div
              key={field.id}
              className="grid grid-cols-3 border-t dark:border-gray-700 text-sm"
            >
              <div className="p-2 border-r dark:border-gray-700">
                <input
                  {...register(`variables.${index}.varName` as const, {
                    required: 'Please enter a value for the variable.',
                  })}
                  className="input px-2 py-1 text-sm"
                />
                {errors.variables?.[index]?.varName && (
                  <p className="required text-sm mt-1">
                    {errors.variables[index]?.varName?.message}
                  </p>
                )}
              </div>
              <div className="p-2 border-r dark:border-gray-700">
                <input
                  {...register(`variables.${index}.varDesc` as const, {
                    required: 'Please enter a value for the variable detail.',
                  })}
                  className="input px-2 py-1 text-sm"
                />
                {errors.variables?.[index]?.varDesc && (
                  <p className="required text-sm mt-1">
                    {errors.variables[index]?.varDesc?.message}
                  </p>
                )}
              </div>
              <div className="p-2 flex justify-center items-center">
                {index !== 0 && (
                  <button
                    type="button"
                    className="btn-secondary p-2"
                    onClick={() => remove(index)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <Controller
          control={control}
          name="emailMessage"
          rules={{
            validate: (value) => {
              const tmp = document.createElement('div');
              tmp.innerHTML = value;
              const text = tmp.innerText.trim();
              return text.length > 0 || 'Email message is required.';
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <div className="mt-1">
                <TextEditor
                  value={value}
                  onChange={(html) => {
                    onChange(html);
                  }}
                />
              </div>
              {error && <p className="error-msg">{error.message}</p>}
            </>
          )}
        />
      </div>
    </>
  );

  if (embedded) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {formFields}
        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800 mt-4">
          <button
            type="button"
            onClick={() => (onClose ? onClose() : navigate('/email'))}
            disabled={isLoading || isUpdating}
            className="px-6 py-2.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isUpdating}
            className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all text-neutral-700 tracking-wide ${
              isLoading || isUpdating
                ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 cursor-not-allowed'
                : 'bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40'
            }`}
          >
            Save
          </button>
        </div>
      </form>
    );
  }

  return (
    <CrudLayout<Inputs>
      id={resolvedId}
      topTitle="Manage System Email"
      title={isEditMode ? 'Update System Email' : 'Add System Email'}
      backTo="/email"
      isLoading={isLoading}
      isUpdating={isUpdating}
      onSubmit={onSubmit}
      handleSubmit={handleSubmit}
    >
      {formFields}
    </CrudLayout>
  );
};

export default SystemEmailForm;
